import { useEffect, useRef, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import * as Crypto from 'expo-crypto';
import Toast from 'react-native-toast-message';
import SheetModal from '@/components/General/SheetModal';
import Input from '@/components/General/inputs/Input';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import Toggle from '@/components/General/inputs/Toggle';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { Mutation } from '@/utils/react-query/Mutations';
import { FOLLOW_UP_TASK_PURPOSES } from '@/utils/constants/followUp';

/**
 * CreateFollowUpTaskSheet — atomic care-task creation via
 * follow_up_create_task (spec §10.2). The RPC creates the Task definition,
 * instance, FollowUpTaskContexts row and initial assignment in one call —
 * the client never chains separate writes.
 *
 * Default assignee is the person's primary caregiver (D07); the assignee
 * picker can target ANY org member (non-team helpers get a limited
 * projection, D31/D32). Leaving the task unassigned surfaces it in Needs
 * Owner for managers (D46). A fresh idempotencyKey per sheet open makes
 * retried submits replay-safe.
 *
 * `careEpisodeID` (optional, §25.9) links the task to a care episode —
 * it then surfaces under the episode's plannedTasks. Omit it for
 * person-level tasks created outside an episode.
 */
const CreateFollowUpTaskSheet = ({ isOpen, onClose, organizationID, personID, personName, primaryCaregiverPersonID, careEpisodeID }) => {
    const idempotencyKeyRef = useRef(null);
    const assigneeEditedRef = useRef(false);

    const [purpose, setPurpose] = useState('OUTREACH');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [dueDate, setDueDate] = useState(null);
    const [assignee, setAssignee] = useState(null);
    const [unassigned, setUnassigned] = useState(false);
    const [safeLogistics, setSafeLogistics] = useState('');

    const { data: caregiver } = Query.people.basicById({
        id: primaryCaregiverPersonID,
        options: { enabled: !!primaryCaregiverPersonID },
    });

    useEffect(() => {
        if (isOpen) {
            idempotencyKeyRef.current = Crypto.randomUUID();
            setPurpose('OUTREACH');
            setTitle('');
            setDescription('');
            setDueDate(null);
            setSafeLogistics('');
            // Default responsibility: the primary caregiver gets new work.
            // When no caregiver exists the task starts unassigned and lands
            // in Needs Owner — never silently assigned elsewhere (D46).
            assigneeEditedRef.current = false;
            setAssignee(primaryCaregiverPersonID ? { id: primaryCaregiverPersonID } : null);
            setUnassigned(!primaryCaregiverPersonID);
        } else {
            idempotencyKeyRef.current = null;
        }
    }, [isOpen, personID]);

    // Late profile data can hydrate the default, never reset an edited form.
    useEffect(() => {
        if (!isOpen || assigneeEditedRef.current) return;
        setAssignee(primaryCaregiverPersonID ? (caregiver || { id: primaryCaregiverPersonID }) : null);
        setUnassigned(!primaryCaregiverPersonID);
    }, [isOpen, primaryCaregiverPersonID, caregiver]);

    const createMutation = Mutation.followUp.createTask({
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'Follow Up task created' });
            onClose?.();
        },
        onError: (error) => {
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to create the task. Please try again.' });
            }
        },
    });

    const submitting = createMutation.isPending;

    const handleSubmit = () => {
        if (submitting) return;
        const payload = {
            personID,
            purpose,
            idempotencyKey: idempotencyKeyRef.current,
        };
        if (careEpisodeID) payload.careEpisodeID = careEpisodeID;
        if (title.trim()) payload.title = title.trim();
        if (description.trim()) payload.description = description.trim();
        if (dueDate) payload.dueDate = new Date(dueDate).toISOString();
        if (safeLogistics.trim()) payload.safeLogistics = safeLogistics.trim();
        if (unassigned) {
            payload.unassigned = true;
        } else if (assignee?.id) {
            payload.assigneePersonID = assignee.id;
        } else {
            // No assignee and not explicitly unassigned — let Needs Owner
            // surface it rather than guessing a fallback (D46).
            payload.unassigned = true;
        }

        createMutation.mutate({ organizationID, personID, careEpisodeID: careEpisodeID ?? null, payload });
    };

    return (
        <SheetModal
            isOpen={isOpen}
            title="Create Follow Up Task"
            subtitle={personName || undefined}
            cancelText="Cancel"
            confirmText="Create Task"
            onCancel={onClose}
            onClose={onClose}
            onConfirm={submitting ? null : handleSubmit}
            loading={submitting}
        >
            <View style={styles.body}>
                <Text style={styles.label}>Purpose</Text>
                <View style={styles.chipRow}>
                    {FOLLOW_UP_TASK_PURPOSES.map((p) => {
                        const selected = purpose === p.value;
                        return (
                            <Pressable
                                key={p.value}
                                testID={`create-task-purpose-${p.value}`}
                                accessibilityRole="button"
                                accessibilityState={{ selected }}
                                onPress={() => setPurpose(p.value)}
                                style={[styles.chip, selected && styles.chipSelected]}
                            >
                                <Unicon name={p.icon} size={14} colorVariant={selected ? 'primary' : 'textSecondary'} />
                                <Text style={[styles.chipText, selected && styles.chipTextSelected]}>{p.label}</Text>
                            </Pressable>
                        );
                    })}
                </View>
                <Text style={styles.purposeHint}>{FOLLOW_UP_TASK_PURPOSES.find(p => p.value === purpose)?.description}</Text>

                <Input
                    title="Title"
                    placeholder={FOLLOW_UP_TASK_PURPOSES.find(p => p.value === purpose)?.label + (personName ? ` for ${personName}` : '')}
                    value={title}
                    maxLength={120}
                    onUpdate={setTitle}
                />

                <Input
                    title="Description (optional)"
                    helpText="Visible to the assignee, including helpers outside the Follow Up team. Use a team note for confidential care context."
                    multiline
                    value={description}
                    onUpdate={setDescription}
                />

                <DatePickerInput
                    title="Due date (optional)"
                    mode="date"
                    value={dueDate}
                    onUpdate={setDueDate}
                />

                <View style={styles.toggleRow}>
                    <Text style={styles.toggleLabel}>Leave unassigned</Text>
                    <Toggle
                        testID="create-task-unassigned-toggle"
                        value={unassigned}
                        onValueChange={(value) => {
                            assigneeEditedRef.current = true;
                            setUnassigned(value);
                        }}
                    />
                </View>
                {!unassigned && (
                    <SheetSelector
                        type="people"
                        title="Assign to"
                        placeholder="Primary connector"
                        value={assignee}
                        onChange={(value) => {
                            assigneeEditedRef.current = true;
                            setAssignee(value);
                        }}
                        excludeIds={personID ? [personID] : []}
                        testID="create-task-assignee-picker"
                    />
                )}
                {unassigned && (
                    <Text style={styles.hint}>Unassigned tasks show in Needs Owner until a manager assigns them.</Text>
                )}

                <Input
                    title="Logistics (optional)"
                    multiline
                    value={safeLogistics}
                    onUpdate={setSafeLogistics}
                />
                <Text style={styles.hint}>Only include what the assignee needs — e.g. a delivery address or time window. Keep care details out of this field.</Text>
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    body: { paddingBottom: 16, gap: 4 },
    label: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        marginTop: 4,
        marginBottom: 6,
    },
    chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    chip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 16,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
    },
    chipSelected: {
        backgroundColor: colors.primaryContainer,
        borderColor: colors.primary,
    },
    chipText: {
        fontSize: 13,
        color: colors.textSecondary,
        fontFamily: fonts.medium,
    },
    chipTextSelected: {
        color: colors.primary,
        fontFamily: fonts.bold,
    },
    purposeHint: {
        fontSize: 12,
        color: colors.textSecondary,
        marginTop: 6,
        fontStyle: 'italic',
    },
    toggleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 8,
    },
    toggleLabel: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.text,
    },
    hint: {
        fontSize: 12,
        color: colors.textSecondary,
        fontStyle: 'italic',
        marginTop: 2,
    },
}));

export default CreateFollowUpTaskSheet;
